import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, Sparkles } from 'lucide-react';
import LoadingState from '../../components/LoadingState';
import Button from '../../components/ui/Button';
import SegmentedControl from '../../components/ui/SegmentedControl';
import SearchInput from '../../components/ui/SearchInput';
import { ErrorState } from '../../components/ui/States';
import {
  fetchRfqDetail,
  fetchRfqItems,
  deleteRfq,
  type RfqDetailDto,
  type RfqItemDto,
  type RfqListItemDto,
  fetchRfqList,
} from '../../lib/rfqApi';
import {
  fetchInboxCounts,
  fetchInboxEmailDetail,
  fetchInboxEmails,
  classifyExistingInboxEmails,
  type InboxCategory,
  type InboxCounts,
  type InboxEmailDetail,
  type InboxEmailListItem,
} from '../../lib/inboxApi';

import { RfqList } from './RfqList';
import { InboxEmailList } from './InboxEmailList';
import { RfqDetail } from './RfqDetail';
import { EmailDetail } from './EmailDetail';
import { NewRfqModal } from './NewRfqModal';
import { RfqItemsModal } from './RfqItemsModal';
import { Modal } from '../../components/Modal';
import { ModuleInfoButton } from '../../components/ModuleInfoButton';
import { useLatestRequest } from '../../hooks/useLatestRequest';
import { useRfqRetry } from './useRfqRetry';
import { initialCategoryFromUrl, initialStringParam } from './inboxUrlState';

const PAGE_SIZE = 25;

const CATEGORY_TABS: Array<{ id: InboxCategory; label: string }> = [
  { id: 'inbox', label: 'All' },
  { id: 'rfq', label: 'RFQ' },
  { id: 'quotation', label: 'Quotations' },
  { id: 'price_list', label: 'Price List' },
  { id: 'confirmed_order', label: 'Confirmed Orders' },
  { id: 'others', label: 'Others' },
];

const STATUS_TABS = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'error', label: 'Error' },
  { id: 'processed', label: 'Processed' },
];

const INBOX_INFO_CONTENT = (
  <div className="flex flex-col gap-2">
    <p>Inbox shows all incoming emails and their assigned category.</p>
    <p>Use the tabs to filter by RFQ, Quotations, Price List, Confirmed Orders, or Others.</p>
  </div>
);

export const RfqInboxPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Guards the [category, status] effect below: it exists to CLEAR a stale
  // selection when the user switches tabs, but must not also clear the
  // selection this same effect's mount run is trying to restore from the URL.
  const didMountRef = useRef(false);

  const [category, setCategory] = useState<InboxCategory>(initialCategoryFromUrl);
  const [counts, setCounts] = useState<InboxCounts | null>(null);

  const [status, setStatus] = useState<'all' | 'pending' | 'error' | 'processed'>(
    () => (initialStringParam('status') as any) || 'all'
  );
  const [rfqs, setRfqs] = useState<RfqListItemDto[]>([]);
  const [rfqStatusCounts, setRfqStatusCounts] = useState<{
    all: number;
    ready: number;
    processing: number;
    processed: number;
    error: number;
    pending: number;
  } | null>(null);
  const [selectedId, setSelectedId] = useState<string | undefined>(() => initialStringParam('selected'));
  const [selectedDetail, setSelectedDetail] = useState<RfqDetailDto | null>(null);
  // D99.13: retry state outlives RfqDetail, which is unmounted on every selection change.
  const currentIdRef = useRef<string | undefined>(undefined);
  currentIdRef.current = selectedId;
  const rfqRetry = useRfqRetry({ currentIdRef, onSucceeded: (_id, quotationId) => navigate(`/quotations/${quotationId}`) });
  const [selectedItems, setSelectedItems] = useState<RfqItemDto[]>([]);

  const [emails, setEmails] = useState<InboxEmailListItem[]>([]);
  const [selectedEmailId, setSelectedEmailId] = useState<string | undefined>(() =>
    initialStringParam('selectedEmail')
  );
  const [selectedEmail, setSelectedEmail] = useState<InboxEmailDetail | null>(null);

  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [newRfqOpen, setNewRfqOpen] = useState(false);
  const [reuploadOpen, setReuploadOpen] = useState(false);
  const [itemsModalOpen, setItemsModalOpen] = useState(false);
  const [classifyOpen, setClassifyOpen] = useState(false);
  const [classifyRunning, setClassifyRunning] = useState(false);
  const [classifyDone, setClassifyDone] = useState(false);
  const [classifyProgress, setClassifyProgress] = useState<{ processed: number; updated: number } | null>(null);
  const [classifyTotal, setClassifyTotal] = useState(0);
  const [classifySummary, setClassifySummary] = useState<Record<string, number>>({});

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');

  const inFlightMoreRef = useRef(false);
  const searchDebounceRef = useRef<number | null>(null);
  const classifyInFlightRef = useRef(false);
  const classifyCancelRef = useRef(false); // set to true to abort the running loop
  // Whose detail response is allowed to reach the pane. One counter for both
  // selection handlers — they write the same pane. TECH_DEBT #46.
  const beginDetailRequest = useLatestRequest();

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const isRfqCategory = category === 'rfq';

  const hasMore = useMemo(() => {
    return (isRfqCategory ? rfqs.length : emails.length) < (total || 0);
  }, [emails.length, isRfqCategory, rfqs.length, total]);

  const loadCounts = async () => {
    try {
      const data = await fetchInboxCounts();
      setCounts(data);
    } catch (e) {
      console.error('Failed to load inbox counts', e);
    }
  };

  const loadRfqList = async (nextStatus = status, searchTerm = search) => {
    try {
      setLoadingList(true);
      setError(null);

      const res = await fetchRfqList(nextStatus as any, 1, PAGE_SIZE, searchTerm);
      setRfqs(res.data || []);
      setPage(res.page || 1);
      setTotal(typeof res.total === 'number' ? res.total : (res.data || []).length);
      if (res.statusCounts) {
        const raw = res.statusCounts as any;
        setRfqStatusCounts({
          all: typeof raw.all === 'number' ? raw.all : 0,
          ready: typeof raw.ready === 'number' ? raw.ready : 0,
          processing: typeof raw.processing === 'number' ? raw.processing : 0,
          processed: typeof raw.processed === 'number' ? raw.processed : 0,
          error: typeof raw.error === 'number' ? raw.error : 0,
          pending: typeof raw.pending === 'number' ? raw.pending : 0,
        });
      }
    } catch (err: any) {
      console.error('Failed to load RFQs:', err);
      setError(err.message || 'Failed to load RFQs');
      setRfqs([]);
      setTotal(0);
      setPage(1);
    } finally {
      setLoadingList(false);
    }
  };

  const loadEmailList = async (searchTerm = search) => {
    try {
      setLoadingList(true);
      setError(null);

      const res = await fetchInboxEmails(category, 1, PAGE_SIZE, searchTerm);
      setEmails(res.data || []);
      setPage(res.page || 1);
      setTotal(typeof res.total === 'number' ? res.total : (res.data || []).length);
    } catch (err: any) {
      console.error('Failed to load inbox emails:', err);
      setError(err.message || 'Failed to load inbox');
      setEmails([]);
      setTotal(0);
      setPage(1);
    } finally {
      setLoadingList(false);
    }
  };

  const loadMore = async () => {
    if (loadingList || loadingMore || inFlightMoreRef.current || !hasMore) return;

    inFlightMoreRef.current = true;
    setLoadingMore(true);

    try {
      const nextPage = page + 1;

      if (isRfqCategory) {
        const res = await fetchRfqList(status as any, nextPage, PAGE_SIZE, search);
        const newRows = res.data || [];
        setRfqs((prev) => {
          const seen = new Set(prev.map((x) => x._id));
          const merged = [...prev];
          for (const row of newRows) {
            if (!seen.has(row._id)) merged.push(row);
          }
          return merged;
        });
        setPage(res.page || nextPage);
        setTotal(typeof res.total === 'number' ? res.total : total);
      } else {
        const res = await fetchInboxEmails(category, nextPage, PAGE_SIZE, search);
        const newRows = res.data || [];
        setEmails((prev) => {
          const seen = new Set(prev.map((x) => x._id));
          const merged = [...prev];
          for (const row of newRows) {
            if (!seen.has(row._id)) merged.push(row);
          }
          return merged;
        });
        setPage(res.page || nextPage);
        setTotal(typeof res.total === 'number' ? res.total : total);
      }
    } catch (err: any) {
      console.error('Failed to load more inbox items:', err);
      window.alert(err.message || 'Failed to load more');
    } finally {
      inFlightMoreRef.current = false;
      setLoadingMore(false);
    }
  };

  // ClickUp 86d42zz72: mirrors the current tab/record into the URL (replacing,
  // not pushing, so every click doesn't grow browser history) so a refresh has
  // something to restore from. Omitting `selected`/`selectedEmail` is how the
  // tab-switch effect below clears the open record from the URL too.
  const syncSelectionToUrl = (params: { selected?: string; selectedEmail?: string }) => {
    const qs = new URLSearchParams();
    qs.set('category', category);
    if (category === 'rfq') qs.set('status', status);
    if (params.selected) qs.set('selected', params.selected);
    if (params.selectedEmail) qs.set('selectedEmail', params.selectedEmail);
    navigate(`/rfq?${qs.toString()}`, { replace: true });
  };

  const shownRfq: any = (selectedDetail as any)?.rfq || selectedDetail;

  const handleSelectRfq = async (id: string) => {
    // Last one wins. Both selection handlers share this counter because they
    // write the SAME pane — clicking an RFQ and then an email is the same race
    // as clicking two RFQs, and the second click must win either way.
    // TECH_DEBT #46.
    const isCurrent = beginDetailRequest();
    setSelectedId(id);
    setSelectedEmailId(undefined);
    syncSelectionToUrl({ selected: id });
    // Clear the pane BEFORE the fetch: if this request fails, the list would
    // otherwise highlight the new row while the pane still shows the old record.
    setSelectedDetail(null);
    setSelectedItems([]);
    setSelectedEmail(null);
    setLoadingDetail(true);
    try {
      const [detail, items] = await Promise.all([fetchRfqDetail(id), fetchRfqItems(id)]);
      if (!isCurrent()) return;
      setSelectedDetail(detail);
      setSelectedItems(items);
    } catch (err: any) {
      if (!isCurrent()) return;
      console.error('Failed to load RFQ detail:', err);
      window.alert(err.message || 'Failed to load RFQ detail');
    } finally {
      // Guarded too: a superseded response clearing this would take the spinner
      // off a request that is still running.
      if (isCurrent()) setLoadingDetail(false);
    }
  };

  // The list row behind the open message. It carries the thread metadata (threadId,
  // messageCount) that GET /inbox/email/:id does not — that endpoint returns one message and
  // knows nothing about the conversation around it. Derived rather than stored so it cannot go
  // stale when the list refreshes under an open detail pane.
  const selectedRow = useMemo(
    () => emails.find((e) => String(e._id) === String(selectedEmailId || '')),
    [emails, selectedEmailId],
  );

  const handleSelectEmail = async (id: string) => {
    const isCurrent = beginDetailRequest();
    setSelectedEmailId(id);
    setSelectedId(undefined);
    syncSelectionToUrl({ selectedEmail: id });
    setSelectedEmail(null);
    setSelectedDetail(null);
    setSelectedItems([]);
    setLoadingDetail(true);
    try {
      const detail = await fetchInboxEmailDetail(id);
      if (!isCurrent()) return;
      setSelectedEmail(detail);
    } catch (err: any) {
      if (!isCurrent()) return;
      console.error('Failed to load email detail:', err);
      window.alert(err.message || 'Failed to load email detail');
    } finally {
      if (isCurrent()) setLoadingDetail(false);
    }
  };

  const handleDeleteRfq = async (id: string) => {
    const confirmed = window.confirm('Are you sure you want to delete this RFQ? This action cannot be undone.');
    if (!confirmed) return;

    try {
      await deleteRfq(id);

      if (selectedId === id) {
        setSelectedId(undefined);
        setSelectedDetail(null);
        setSelectedItems([]);
        // Otherwise a refresh right after deleting would restore-fetch a
        // record that no longer exists (86d42zz72 made the URL durable, so a
        // stale one has to be cleaned up same as the state it mirrors).
        syncSelectionToUrl({});
      }

      await loadRfqList(status);
      await loadCounts();
    } catch (err: any) {
      console.error('Failed to delete RFQ:', err);
      window.alert(err.message || 'Failed to delete RFQ');
    }
  };

  const refreshSelected = async () => {
    if (isRfqCategory) {
      await loadRfqList(status);
      if (selectedId) await handleSelectRfq(selectedId);
    } else {
      await loadEmailList(search);
      if (selectedEmailId) await handleSelectEmail(selectedEmailId);
    }
    await loadCounts();
  };

  // D103.15: an upload finishes long after the form closed, when the user may be on another filter or
  // RFQ. The modals get this stable callback, which always runs the page's CURRENT refreshSelected.
  const refreshSelectedRef = useRef(refreshSelected);
  refreshSelectedRef.current = refreshSelected;
  const refreshCurrent = useCallback(() => refreshSelectedRef.current(), []);

  const handleEmailUpdated = async (updated: InboxEmailDetail) => {
    setSelectedEmail(updated);
    await loadCounts();
    if (!isRfqCategory) {
      await loadEmailList(search);
    }
  };

  const handleRfqEmailReclassified = async () => {
    // If RFQ email is moved away, ensure it disappears from RFQ list immediately.
    if (selectedId) {
      setSelectedId(undefined);
      setSelectedDetail(null);
      setSelectedItems([]);
      syncSelectionToUrl({});
    }
    await loadRfqList(status);
    await loadCounts();
  };

  const cancelInboxClassification = () => {
    classifyCancelRef.current = true;
  };

  const runInboxClassification = async () => {
    if (classifyInFlightRef.current) return;
    classifyInFlightRef.current = true;
    classifyCancelRef.current = false; // reset for new run
    setClassifyRunning(true);
    setClassifyDone(false);
    setClassifyProgress({ processed: 0, updated: 0 });
    setClassifySummary({});
    const freshCounts = await fetchInboxCounts().catch(() => counts);
    if (freshCounts) setCounts(freshCounts as InboxCounts);
    // NOT counts.inbox. That badge counts CONVERSATIONS (it must — it labels a
    // tab that lists conversations), while classification walks MESSAGES: 1,041
    // against 1,344 on QA, so the denominator sat below the real work and the
    // bar pinned at 100% for the tail. It was wrong before the badge was fixed
    // too, in the other direction (2,569). The classify endpoint reports the
    // count of what it is about to iterate; that arrives with the first batch
    // below. TECH_DEBT #47.
    setClassifyTotal(0);

    try {
      let afterId: string | null = null;
      let totalProcessed = 0;
      let totalUpdated = 0;
      let hasMore = true;
      const batchSize = 10;
      const summary: Record<string, number> = {};

      while (hasMore) {
        // Check for cancel before each batch
        if (classifyCancelRef.current) break;

        const res: any = await classifyExistingInboxEmails({
          scope: 'all',
          limit: batchSize,
          afterId,
          force: true,
        });

        // Sent on the first page only, and it is the real denominator: messages
        // in scope for this sweep, counted by the endpoint doing the sweeping.
        if (typeof res?.data?.total === 'number') setClassifyTotal(res.data.total);

        const processed = res?.data?.processed || 0;
        const updated = res?.data?.updated || 0;
        totalProcessed += processed;
        totalUpdated += updated;
        setClassifyProgress({ processed: totalProcessed, updated: totalUpdated });

        const results = Array.isArray(res?.data?.results) ? res.data.results : [];
        for (const row of results) {
          const labels = Array.isArray(row?.labels) && row.labels.length ? row.labels : ['others'];
          for (const label of labels) {
            const key = String(label || 'others');
            summary[key] = (summary[key] || 0) + 1;
          }
        }
        setClassifySummary({ ...summary });

        afterId = res?.data?.nextAfterId || null;
        hasMore = !!res?.data?.hasMore;
        if (!processed) break;

        // Gap between batches — also check cancel periodically during the wait
        const gap = 500 + Math.floor(Math.random() * 400);
        const slice = 100;
        for (let waited = 0; waited < gap; waited += slice) {
          if (classifyCancelRef.current) break;
          await sleep(Math.min(slice, gap - waited));
        }
      }

      await loadCounts();
      if (!isRfqCategory) {
        await loadEmailList(search);
      }
      if (selectedEmailId) {
        await handleSelectEmail(selectedEmailId);
      }

      setClassifyDone(true);
    } catch (e: any) {
      console.error('Failed to classify emails', e);
      window.alert(e?.message || 'Failed to classify emails');
    } finally {
      setClassifyRunning(false);
      classifyInFlightRef.current = false;
    }
  };

  useEffect(() => {
    loadCounts();
  }, []);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      // First run: category/status/selection just came from the URL (or from
      // their defaults) — load the matching list and, if a record was in the
      // URL, restore its detail pane too. Falling through to the reset below
      // would immediately wipe the very selection this run is restoring.
      setPage(1);
      setTotal(0);
      if (isRfqCategory) {
        loadRfqList(status);
        if (selectedId) handleSelectRfq(selectedId);
      } else {
        loadEmailList(search);
        if (selectedEmailId) handleSelectEmail(selectedEmailId);
      }
      return;
    }

    setSelectedId(undefined);
    setSelectedDetail(null);
    setSelectedItems([]);
    setSelectedEmailId(undefined);
    setSelectedEmail(null);

    setPage(1);
    setTotal(0);
    if (isRfqCategory) {
      loadRfqList(status);
    } else {
      loadEmailList(search);
    }
    // A real tab switch abandons whatever record was open, so the URL drops
    // selected/selectedEmail along with it rather than pointing at a record
    // no longer visible on this tab.
    syncSelectionToUrl({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, status]);

  useEffect(() => {
    if (searchDebounceRef.current) window.clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = window.setTimeout(() => {
      setPage(1);
      setTotal(0);
      if (isRfqCategory) {
        loadRfqList(status, search);
      } else {
        loadEmailList(search);
      }
    }, 350);

    return () => {
      if (searchDebounceRef.current) window.clearTimeout(searchDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (classifyOpen) {
      runInboxClassification();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classifyOpen]);

  // Handles a navigation to this SAME route with a different `?selected=`/
  // `?selectedEmail=` — e.g. a dashboard notification link clicked while
  // already on Inbox — which re-renders rather than remounting, so the lazy
  // state initializers above never run again. Guarded against the id already
  // matching current state, since our OWN replace-navigations in
  // syncSelectionToUrl land here too and must not re-trigger a refetch of
  // what was just selected.
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const selected = qs.get('selected') || (location.state as any)?.selectedRfqId;
    const selectedEmail = qs.get('selectedEmail');
    if (selected && selected !== selectedId) {
      setCategory('rfq');
      setStatus('all');
      handleSelectRfq(selected);
    } else if (selectedEmail && selectedEmail !== selectedEmailId) {
      handleSelectEmail(selectedEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return (
    <>
      <div className="h-full flex flex-col bg-brand-bg">
        {/* The app's header band. The canvas was its own green-grey (#f2f6f3),
            one of six the modules used. */}
        <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-4 sm:px-8">
          {/* Single row: title | chips (centered) | action buttons */}
          <div className="flex flex-wrap items-center gap-3">

            {/* Left: Inbox label */}
            <div className="flex items-center gap-2 shrink-0">
              <h1 className="text-xl font-semibold leading-7 text-slate-900">Inbox</h1>
              <ModuleInfoButton content={INBOX_INFO_CONTENT} ariaLabel="Inbox information" />
            </div>

            {/* Center: category chips — flex-1 so they fill the space and stay centered */}
            <div className="flex flex-1 flex-wrap items-center justify-center gap-2">
              <SegmentedControl
                aria-label="Inbox category"
                value={category}
                onChange={setCategory}
                segments={CATEGORY_TABS.map((tab) => ({
                  value: tab.id,
                  label: tab.label,
                  count: Number((counts as any)?.[tab.id]) || 0,
                }))}
              />
            </div>

            {/* Right: action buttons */}
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              <Button variant="primary" onClick={() => setNewRfqOpen(true)} icon={<Plus aria-hidden="true" className="h-4 w-4" />}>
                New RFQ
              </Button>
              <Button onClick={() => setClassifyOpen(true)} icon={<Sparkles aria-hidden="true" className="h-4 w-4" />}>
                Classify with AI
              </Button>
            </div>
          </div>
        </div>

        <div className="flex flex-1 min-h-0 px-4 pb-6 pt-4 sm:px-8">
          <div className="flex w-full min-h-0 overflow-hidden rounded-lg border border-slate-200 bg-white">
            <div className="flex w-[360px] flex-col min-h-0 border-r border-slate-200 bg-slate-50">
              <div className="border-b border-slate-200 p-3">
                <SearchInput value={search} onChange={setSearch} placeholder="Search here" className="w-full" />

                {isRfqCategory && (
                  <div className="mt-2 flex items-center gap-2 text-xs">
                    {STATUS_TABS.map((t) => {
                      const statusCounts = rfqStatusCounts || {
                        all: 0,
                        ready: 0,
                        processing: 0,
                        processed: 0,
                        error: 0,
                        pending: 0,
                      };
                      const countForTab =
                        t.id === 'all'
                          ? statusCounts.all
                          : t.id === 'pending'
                          ? statusCounts.pending
                          : t.id === 'processed'
                          ? statusCounts.processed
                          : statusCounts.error;
                      return (
                        <button
                          key={t.id}
                          onClick={() => setStatus(t.id as any)}
                          className={[
                            'px-2 py-1 rounded-md border text-xs font-semibold',
                            status === t.id
                              ? 'bg-primary-500 text-white border-primary-500'
                              : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50',
                          ].join(' ')}
                        >
                          {t.label} <span className="ml-1 text-xs opacity-80">{countForTab}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {loadingList ? (
                <div className="flex-1 flex items-center justify-center">
                  <LoadingState text="Loading inbox..." />
                </div>
              ) : error ? (
                <div className="p-3">
                  <ErrorState
                    message={error}
                    onRetry={() => (isRfqCategory ? loadRfqList(status) : loadEmailList(search))}
                  />
                </div>
              ) : isRfqCategory ? (
                <RfqList
                  rfqs={rfqs}
                  selectedId={selectedId}
                  onSelect={handleSelectRfq}
                  onDelete={handleDeleteRfq}
                  onLoadMore={loadMore}
                  hasMore={hasMore}
                  loadingMore={loadingMore}
                  total={total}
                />
              ) : (
                <InboxEmailList
                  items={emails}
                  selectedId={selectedEmailId}
                  onSelect={handleSelectEmail}
                  onLoadMore={loadMore}
                  hasMore={hasMore}
                  loadingMore={loadingMore}
                  total={total}
                />
              )}
            </div>

            <div className="flex-1 min-w-0">
              {loadingDetail && (selectedId || selectedEmailId) ? (
                <div className="h-full flex items-center justify-center bg-white">
                  <LoadingState text="Loading email..." />
                </div>
              ) : isRfqCategory ? (
                <RfqDetail
                  rfq={selectedDetail}
                  items={selectedItems}
                  onOpenItemsModal={() => setItemsModalOpen(true)}
                  onUploadRfqClick={() => setReuploadOpen(true)}
                  onDeleteRfq={handleDeleteRfq}
                  onEmailReclassified={handleRfqEmailReclassified}
                  retry={rfqRetry.view(shownRfq?._id, shownRfq?.generationError)}
                  onRetry={rfqRetry.start}
                />
              ) : (
                <EmailDetail
                  email={selectedEmail}
                  threadId={selectedRow?.threadId}
                  messageCount={selectedRow?.messageCount}
                  threadInboxTypes={selectedRow?.inboxTypes}
                  onEmailUpdated={handleEmailUpdated}
                  onEmailDeleted={async () => {
                    setSelectedEmail(null);
                    setSelectedEmailId(undefined);
                    syncSelectionToUrl({});
                    await loadEmailList(search);
                    await loadCounts();
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <NewRfqModal
        isOpen={newRfqOpen}
        onClose={() => setNewRfqOpen(false)}
        onCreated={refreshCurrent}
        mode="create"
      />

      <NewRfqModal
        isOpen={reuploadOpen}
        onClose={() => setReuploadOpen(false)}
        onCreated={refreshCurrent}
        mode="reupload"
        targetRfqId={selectedDetail?._id}
      />

      <RfqItemsModal
        isOpen={itemsModalOpen}
        onClose={() => setItemsModalOpen(false)}
        rfq={selectedDetail}
        items={selectedItems}
      />

      <Modal
        isOpen={classifyOpen}
        onClose={() => {
          // X button: if running, abort the loop AND close
          if (classifyRunning) cancelInboxClassification();
          setClassifyOpen(false);
        }}
        title="Classify with AI"
        maxWidthClass="max-w-lg"
      >
        <div className="space-y-4">
          {!classifyDone ? (
            <>
              <div className="text-xs text-slate-600">
                Processing emails and moving them to the right category.
              </div>

              <div className="space-y-2">
                <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-2 rounded-full bg-primary-500 transition-all"
                    style={{
                      width: (() => {
                        if (!classifyProgress) return '0%';
                        const total = Math.max(classifyTotal || 0, classifyProgress.processed || 0);
                        if (!total) return '0%';
                        return `${Math.min(100, Math.round((classifyProgress.processed / total) * 100))}%`;
                      })(),
                    }}
                  />
                </div>
                <div className="text-xs text-slate-600">
                  {classifyProgress
                    ? `Processed ${classifyProgress.processed} of ${Math.max(
                        classifyTotal || 0,
                        classifyProgress.processed || 0
                      )} • Updated ${classifyProgress.updated}`
                    : 'Starting...'}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 text-sm font-semibold text-cyan-700">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-cyan-100 text-cyan-700">
                  ✓
                </span>
                Classification complete
              </div>
              <div className="text-xs text-slate-600">Summary by category</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {Object.keys(classifySummary).length === 0 ? (
                  <div className="text-slate-500">No categories updated.</div>
                ) : (
                  Object.entries(classifySummary).map(([key, value]) => (
                    <div
                      key={key}
                      className="flex items-center justify-between rounded border border-slate-200 bg-slate-50 px-3 py-2"
                    >
                      <span className="font-semibold text-slate-700">{key}</span>
                      <span className="text-slate-600">{value}</span>
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                // Cancel: if running → abort the loop; either way close the modal
                if (classifyRunning) cancelInboxClassification();
                setClassifyOpen(false);
              }}
              className="px-3 py-2 rounded-md border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50"
            >
              {classifyDone ? 'Done' : classifyRunning ? 'Stop & Close' : 'Cancel'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};
